/**
 * Vuex storage plugin
 * LocalStorage for runtime store values.
 * @param  {object} store [Vuex store object]
 */
const storeStorage = (store) => {

    /**
     * Restore the runtime state after the application is loaded.
     */
    const restoreMobileRuntimeState = async () => {
        try {
            // get viewport properties from local storage
            const viewportData = JSON.parse(localStorage.getItem('VIEWPORT_DATA_KEY'));
            if (viewportData && Object.keys(viewportData).length > 0) {
                await store.dispatch('viewportZoom', viewportData.zoom);
                await store.dispatch('viewportCenter', viewportData.center);
            }
            // previous loaded themes
            const themesData = JSON.parse(localStorage.getItem('THEME_DATA_KEY'));
            if (themesData && Object.keys(themesData).length > 0) {
                await store.dispatch('loadedThemeNames', themesData.themes);
            }
            // previous loaded feedback-themes
            const feedbackData = JSON.parse(localStorage.getItem('FEEDBACK_DATA_KEY'));
            if (feedbackData && Object.keys(feedbackData).length > 0) {
                await store.dispatch('loadedFeedbackNames', feedbackData.feedbacks);
            }

        } catch (error) {
            console.log(`restoreMobileRuntimeState: ${error}`);
        } finally {
            store.dispatch('runtimeStateRestored', true);
        }
    };


    /**
     * Restore the runtime state after the application is loaded.
     */
    const restoreWebRuntimeState = () => {
        try {
            // create new session id
            const sessionId = Math.floor(Math.random() * 100000000000).toString();

            // get userInfo from the store
            const userInfo = store.getters.userInfo;
            userInfo.sessionId = sessionId;
            store.commit('SET_USERINFO', userInfo);
        } catch (error) {
            console.log(`restoreMobileRuntimeState: ${error}`);
        } finally {
            store.dispatch('runtimeStateRestored', true);
        }
    };

    /**
     * Store viewport state.
     */
    const storeViewportState = (state) => {
        try {
            const viewportZoom = state.map.getView().getZoom();
            const viewportCenter = state.map.getView().getCenter();
            // do not store null values
            if (viewportZoom && viewportCenter) {
                store.dispatch('viewportZoom', viewportZoom);
                store.dispatch('viewportCenter', viewportCenter);
                // create object to store in localStorage
                const viewportData = {
                    center: state.runtime.viewportCenter,
                    zoom: state.runtime.viewportZoom,
                };
                // set in local storage
                localStorage.setItem('VIEWPORT_DATA_KEY', JSON.stringify(viewportData));
            }
        } catch (error) {
            console.log(`storeViewportState: ${error}`);
        }
    };

    /**
     * Store theme state.
     */
    const storeThemeState = (state) => {
        try {
            // get names of loaded themes
            const visibleLoadedThemes = state.loadedThemes.filter(theme => {
                return theme.loaded === true;
            });
            const themeNames = visibleLoadedThemes.map((item) => {
                return item.name;
            });
            store.dispatch('loadedThemeNames', themeNames);
            // create object to store in localStorage
            const themeData = { themes: state.runtime.loadedThemeNames, };
            // set in local storage
            localStorage.setItem('THEME_DATA_KEY', JSON.stringify(themeData));
        } catch (error) {
            console.log(`storeThemeState: ${error}`);
        }
    };

    /**
     * Store feedback state.
     */
    const storeFeedbackState = (state) => {
        try {
            const visibleLoadedFeedbacks = state.loadedFeedbacks.filter(feedback => {
                return feedback.loaded === true;
            });
            // get names of loaded feedbacks
            const feedbackNames = visibleLoadedFeedbacks.map((item) => {
                return item.name;
            });
            store.dispatch('loadedFeedbackNames', feedbackNames);
            // create object to store in localStorage
            const feedbackData = { feedbacks: state.runtime.loadedFeedbackNames, };
            // set in local storage
            localStorage.setItem('FEEDBACK_DATA_KEY', JSON.stringify(feedbackData));
        } catch (error) {
            console.log(`storeFeedbackState: ${error}`);
        }
    };

    /**
     *
     * @param  {[type]} state [description]
     * @return {[type]}       [description]
     */
    const storeRuntimeState = (mutation, state) => {
        // zoom- or pan action
        if (mutation.type === 'MAP_ZOOMED' || mutation.type === 'MAP_MOVEEND') {
            storeViewportState(state);
            return;
        }
        // add- or update- or removed theme action
        if (mutation.type === 'LOADED_THEMES_ADD' || mutation.type === 'LOADED_THEMES_UPDATE' || mutation.type === 'REMOVED_THEME') {
            storeThemeState(state);
            return;
        }
        // add- or update- or removed feedback action
        if (mutation.type === 'LOADED_FEEDBACKS_ADD' || mutation.type === 'LOADED_FEEDBACKS_UPDATE' || mutation.type === 'REMOVED_FEEDBACK') {
            storeFeedbackState(state);
        }
    };

    /**
     * Called after the Vuex store is initialized.
     * Listen for runtime store mutations
     * @param {object}  mutation    [containing mutation name and payload]
     * @param {object}  state       [current store state]
     */
    store.subscribe((mutation, state) => {
        if (store.getters.isMobile) {
            // application/map is initialized
            if ((mutation.type === 'MAPLAYERS_LOADED' || mutation.type === 'NO_BASELAYER_FORCE_RELOAD') && !state.runtime.runtimeStateRestored) {
                // restore state from local storage
                restoreMobileRuntimeState();
                return;
            }
            if (state.runtime.runtimeStateRestored) {
                storeRuntimeState(mutation, state);
            }
        } else {
            // web application runtime state
            if (mutation.type === 'MAPLAYERS_LOADED' && !state.runtime.runtimeStateRestored) {
                // restore state from local storage
                restoreWebRuntimeState();
            }
        }
    });

};

export default storeStorage;
